import { JSONParser } from '@streamparser/json';
import axios from 'axios';
import { getPosthogSessionInfo } from '../hooks/tracking/usePosthogSessionInfo';

const api = <T>(fn: Function, args: any[]): Promise<T> =>
  fn.apply(axios, args).then(
    (res: any) => res?.data,
    (res: any) => Promise.reject(res),
  );

export const get = <T = any>(url: string, params = {}, config?: object) =>
  api<T>(axios.get, [url, { params, ...(config ?? {}) }]);

export const post = <T = any>(url: string, body?: object, config?: object) =>
  api<T>(axios.post, [url, body, config]);

export const put = <T = any>(url: string, body: object) =>
  api<T>(axios.put, [url, body]);

export const patch = <T = any>(url: string, body: object, config?: object) =>
  api<T>(axios.patch, [url, body, config]);

export const del = <T = any>(url: string, body?: object) =>
  api<T>(axios.delete, [url, body]);

export const getStreamingJSON = async <T = any>(
  url: string,
  params = {},
  accessToken?: string,
): Promise<T> => {
  try {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    const fullUrl = searchParams.toString() ? `${url}?${searchParams}` : url;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const { sessionId, sessionUrl } = getPosthogSessionInfo();
    if (sessionId) {
      headers['X-PostHog-Session-ID'] = sessionId;
    }
    if (sessionUrl) {
      headers['X-PostHog-Session-URL'] = sessionUrl;
    }

    const response = await fetch(fullUrl, { headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
      throw new Error('Response body is not available for streaming');
    }

    return await new Promise<T>((resolve, reject) => {
      let result: T;
      let hasResult = false;

      const parser = new JSONParser();

      parser.onValue = (value: any) => {
        result = value as T;
        hasResult = true;
      };

      parser.onEnd = () => {
        if (hasResult) {
          resolve(result);
        } else {
          reject(new Error('No JSON data parsed'));
        }
      };

      parser.onError = (error: any) => {
        reject(error);
      };

      const reader = response.body!.getReader();

      function pump(): void {
        reader
          .read()
          .then(({ done, value }) => {
            if (done) {
              parser.end();
              return;
            }

            parser.write(value);
            pump();
          })
          .catch(reject);
      }

      pump();
    });
  } catch (error) {
    return api<T>(axios.get, [url, { params }]);
  }
};

export const delay = (data: object, millisecs = 1000): Promise<any> =>
  new Promise((resolve) => {
    setTimeout(
      () => resolve(typeof data === 'function' ? data() : data),
      millisecs,
    );
  });
